import { Navigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { logo } from '@clofo/ui/images'
import { usePageTitle } from '@clofo/ui/hooks'
import { useTranslation, Trans } from '@clofo/i18n'

import { OtpForm } from '../components/otp-form'
import { getTfaSession } from '../lib/tfa-session'

const externalLink = (href: string) => (
  <a
    className='text-primary font-medium hover:underline'
    href={href}
    target='_blank'
    rel='noreferrer noopener'
  />
)

/**
 * First-time TFA setup after password login (tfa_status 'enabled' — the counter-intuitive legacy
 * name for "not configured yet"): authenticator-app recommendations, the QR code from the obtain
 * token's tfa_secret_uri, and OTP entry. Guarded — the legacy pages were not: direct navigation
 * with no pending TFA session (including an 'enabled' token missing its tfa_secret_uri) returns
 * to the login page, and an already-configured session ('enforced') is re-routed to /tfa/verify,
 * mirroring the legacy auth machine's transitionToTfa action.
 */
export const TfaConfirmPage = () => {
  const { t } = useTranslation()
  usePageTitle(t('login.tfa.orgRequired'))

  const session = getTfaSession()
  if (!session) return <Navigate to='/' replace />
  if (session.status === 'enforced') return <Navigate to='/tfa/verify' replace />

  return (
    <main className='flex flex-col bg-background items-center justify-center'>
      <img src={logo} alt='Closing Folders' className='my-13 h-15' />
      <div className='w-full max-w-2xl px-4 pb-12'>
        <h1 className='text-xl font-semibold mb-4'>{t('login.tfa.orgRequired')}</h1>
        <p className='mb-6'>{t('login.tfa.introBlurb')}</p>

        <h2 className='font-semibold mb-2'>{t('login.tfa.step1')}</h2>
        <p className='mb-6'>
          <Trans
            i18nKey='login.tfa.step1Body'
            components={{
              googleLink: externalLink('https://support.google.com/accounts/answer/1066447?hl=en'),
              msLink: externalLink(
                'https://www.microsoft.com/en-ca/security/mobile-authenticator-app',
              ),
              duoLink: externalLink(
                'https://duo.com/product/multi-factor-authentication-mfa/duo-mobile-app#download',
              ),
              onePassLink: externalLink('https://support.1password.com/one-time-passwords/'),
            }}
          />
        </p>

        <h2 className='font-semibold mb-2'>{t('login.tfa.step2')}</h2>
        <QRCodeSVG
          value={session.tfaSecretUri}
          size={150}
          role='img'
          aria-label={t('login.tfa.qrCodeAlt')}
        />

        <h2 className='font-semibold mb-2 mt-6'>{t('login.tfa.step3')}</h2>
        <OtpForm hideLabel />
      </div>
    </main>
  )
}
